/**
 * <ProgressBar> — horizontal fill bar (atom, L0.1).
 *
 * A rounded track with a colored fill representing a 0–100 percentage. Used for
 * session/activity progress, assessment preparation, chapter completion, etc.
 *
 * No hex literals: `color` / `trackColor` are semantic tokens read from the theme.
 */
import React from "react"
import { View, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { type RadiusKey } from "../../theme/tokens/radius"

export type ProgressBarProps = {
    /** Fill percentage, 0–100 (clamped). */
    value: number
    /** Fill color token — defaults to `primary`. */
    color?: SemanticColorKey
    /** Track (background) color token — defaults to `surface-subtle`. */
    trackColor?: SemanticColorKey
    /** Bar thickness in px — defaults to 8. */
    height?: number
    /** Corner radius token — defaults to `sm`. */
    radius?: RadiusKey
    style?: StyleProp<ViewStyle>
    testID?: string
}

/** L'épaisseur par défaut de la barre : son squelette la reprend. */
export const PROGRESS_BAR_HEIGHT = 8

export function ProgressBar(props: ProgressBarProps) {
    const {
        value,
        color = "primary",
        trackColor = "surface-subtle",
        height = PROGRESS_BAR_HEIGHT,
        radius = "sm",
        style,
        testID,
    } = props

    const pct = Math.max(0, Math.min(100, value))

    return (
        <View testID={testID} style={[styles.track(trackColor, height, radius), style]}>
            <View style={styles.fill(color, pct, radius)} />
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    track: (trackColor: SemanticColorKey, height: number, radius: RadiusKey) => ({
        width: "100%" as const,
        height,
        borderRadius: theme.radius[radius],
        backgroundColor: theme.colors[trackColor],
        overflow: "hidden" as const,
    }),
    fill: (color: SemanticColorKey, pct: number, radius: RadiusKey) => ({
        width: `${pct}%` as `${number}%`,
        height: "100%" as const,
        borderRadius: theme.radius[radius],
        backgroundColor: theme.colors[color],
    }),
}))
